import { TIANDITU_KEY, TIANDITU_VERSION } from "../constants";

let loadPromise = null;

export const DEFAULT_MAP_CENTER = {
  longitude: 87.68,
  latitude: 43.77,
};

export function loadTianditu() {
  if (typeof window !== "undefined" && window.T) {
    return Promise.resolve(window.T);
  }

  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src = `https://api.tianditu.gov.cn/api?v=${TIANDITU_VERSION}&tk=${TIANDITU_KEY}`;
    script.onload = () => {
      if (window.T) {
        resolve(window.T);
      } else {
        reject(new Error("Tianditu API failed to load"));
      }
    };
    script.onerror = () => reject(new Error("Tianditu script load failed"));
    document.head.appendChild(script);
  });

  return loadPromise;
}

export function parseLngLat(value) {
  if (!value) {
    return null;
  }

  const parts = String(value)
    .split(",")
    .map((item) => parseFloat(item.trim()));

  if (parts.length < 2 || Number.isNaN(parts[0]) || Number.isNaN(parts[1])) {
    return null;
  }

  return { longitude: parts[0], latitude: parts[1] };
}

export function parseLocalSearchPois(result) {
  if (!result || typeof result.getPois !== "function") {
    return [];
  }

  const pois = result.getPois();
  if (!pois || !pois.length) {
    return [];
  }

  return pois
    .map((poi) => {
      const lonlat = poi.lonlat || poi.lonLat || "";
      const [lng, lat] = String(lonlat)
        .split(/[\s,]+/)
        .map(Number);

      if (Number.isNaN(lng) || Number.isNaN(lat)) {
        return null;
      }

      return {
        adname: poi.adname || poi.county || "",
        cityname: poi.cityname || poi.city || "",
        location: { lng, lat },
        name: poi.name,
        photos: poi.pic ? [{ url: poi.pic }] : [],
        address: poi.address || poi.name,
        checked: true,
      };
    })
    .filter(Boolean);
}

export function searchPoisInBounds(map, bounds, keyword = "住宅") {
  return loadTianditu().then(
    (T) =>
      new Promise((resolve) => {
        const localSearch = new T.LocalSearch(map, {
          pageCapacity: 10,
          onSearchComplete: (result) => {
            resolve(parseLocalSearchPois(result));
          },
        });
        localSearch.searchInBounds(keyword, bounds);
      })
  );
}

function createMapInstance(map, marker, clickHandler) {
  return {
    map,
    marker,
    resize() {
      if (typeof map.checkResize === "function") {
        map.checkResize();
      }
    },
    destroy() {
      if (clickHandler) {
        map.removeEventListener("click", clickHandler);
      }
      map.clearOverLays();
    },
  };
}

export function createStaticMarkerMap({
  containerId,
  longitude,
  latitude,
  zoom,
}) {
  return loadTianditu().then((T) => {
    const lng = parseFloat(longitude);
    const lat = parseFloat(latitude);

    if (Number.isNaN(lng) || Number.isNaN(lat)) {
      throw new Error("Invalid map coordinates");
    }

    const map = new T.Map(containerId);
    map.centerAndZoom(new T.LngLat(lng, lat), zoom);

    const marker = new T.Marker(new T.LngLat(lng, lat));
    map.addOverLay(marker);

    return createMapInstance(map, marker);
  });
}

export function createClickableMarkerMap({
  containerId,
  longitude,
  latitude,
  zoom,
  onPositionChange,
}) {
  return loadTianditu().then((T) => {
    const lng = parseFloat(longitude);
    const lat = parseFloat(latitude);

    if (Number.isNaN(lng) || Number.isNaN(lat)) {
      throw new Error("Invalid map coordinates");
    }

    const map = new T.Map(containerId);
    map.centerAndZoom(new T.LngLat(lng, lat), zoom);

    const marker = new T.Marker(new T.LngLat(lng, lat));
    map.addOverLay(marker);

    const handleClick = (event) => {
      const lnglat = event.lnglat;
      if (!lnglat) {
        return;
      }

      const nextLng =
        typeof lnglat.getLng === "function" ? lnglat.getLng() : lnglat.lng;
      const nextLat =
        typeof lnglat.getLat === "function" ? lnglat.getLat() : lnglat.lat;
      marker.setLngLat(new T.LngLat(nextLng, nextLat));
      onPositionChange?.({
        longitude: nextLng,
        latitude: nextLat,
      });
    };

    map.addEventListener("click", handleClick);

    return createMapInstance(map, marker, handleClick);
  });
}

export function createSquarePolygonPoints(T, longitude, latitude, offset = 0.001) {
  const lng = parseFloat(longitude);
  const lat = parseFloat(latitude);

  return [
    new T.LngLat(lng + offset, lat + offset),
    new T.LngLat(lng + offset, lat - offset),
    new T.LngLat(lng - offset, lat - offset),
    new T.LngLat(lng - offset, lat + offset),
  ];
}
